/**
 * WBS Status Matrix — real-time floor × stage achievement grid for one building.
 *
 * Column 1 = Floor; remaining columns = FLOOR-scoped WBS stages (catalog order).
 * Cell: green check when achieved (title = achieved date), amber date when a
 * target is planned but not achieved, "—" otherwise.
 * BUILDING/PROJECT-scoped stages render separately as a chips row above the
 * matrix (they are not per-floor).
 *
 * Pure presentational — derives everything from props (no fetching), so it
 * stays in sync with whatever the parent (BuildingStatus) last fetched.
 */
import React from "react";
import moment from "moment";
import { Check } from "lucide-react";
import { WBS_SCOPE, WBS_TYPE } from "../../constants/wbsStages";

// e.g. 07 Jun '26 — short year so target chips stay compact in matrix cells
const formatShort = (date) => (date ? moment(date).format("DD MMM 'YY") : "—");

function WbsStatusMatrix({ stages, events, numberOfFloors }) {
  const floorStages = stages.filter(
    (s) => s.isActive && s.isConstruction && s.scope === WBS_SCOPE.FLOOR
  );
  const buildingStages = stages.filter(
    (s) =>
      s.isActive &&
      s.isConstruction &&
      s.scope !== WBS_SCOPE.FLOOR &&
      s.typeName !== WBS_TYPE.CUSTOM
  );

  // Index events: floor-scoped by "stageId-floor", building-scoped by stageId.
  const byStageFloor = {};
  const byStage = {};
  (events || []).forEach((ev) => {
    const stageId = ev.wbsStage?.wbsStageId;
    if (stageId === null || stageId === undefined) return;
    if (ev.floorNumber !== null && ev.floorNumber !== undefined) {
      byStageFloor[`${stageId}-${ev.floorNumber}`] = ev;
    } else {
      byStage[stageId] = ev;
    }
  });

  // Top floor first (descending), ground last — mirrors the physical building
  const floors = Array.from({ length: (numberOfFloors || 0) + 1 }, (_, i) => (numberOfFloors || 0) - i);

  const renderCell = (event) => {
    if (event?.achievedDate) {
      return (
        <span
          title={`Achieved ${moment(event.achievedDate).format("DD MMM YYYY")}`}
          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700"
        >
          <Check size={14} strokeWidth={3} />
        </span>
      );
    }
    if (event?.plannedDate || event?.targetStartDate) {
      const windowTitle = [
        event.targetStartDate ? `start ${moment(event.targetStartDate).format("DD MMM YYYY")}` : null,
        event.plannedDate ? `end ${moment(event.plannedDate).format("DD MMM YYYY")}` : null,
      ].filter(Boolean).join(" → ");
      return (
        <span
          title={`Target ${windowTitle}`}
          className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700"
        >
          {formatShort(event.plannedDate || event.targetStartDate)}
        </span>
      );
    }
    return <span className="text-slate-300">—</span>;
  };

  return (
    <div className="space-y-3">
      {/* BUILDING/PROJECT-scoped statuses — shown separately from the floor matrix */}
      <div className="flex flex-wrap gap-2">
        {buildingStages.map((stage) => {
          const event = byStage[stage.wbsStageId] || byStageFloor[`${stage.wbsStageId}-0`];
          const achieved = !!event?.achievedDate;
          const planned = !achieved && !!event?.plannedDate;
          return (
            <span
              key={stage.wbsStageId}
              title={
                achieved
                  ? `Achieved ${moment(event.achievedDate).format("DD MMM YYYY")}`
                  : planned
                    ? `Target ${moment(event.plannedDate).format("DD MMM YYYY")}`
                    : "Not started"
              }
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${achieved
                ? "bg-emerald-100 text-emerald-700"
                : planned
                  ? "bg-amber-100 text-amber-700"
                  : "bg-slate-100 text-slate-500"
                }`}
            >
              {achieved && <Check size={12} strokeWidth={3} />}
              <span>{stage.label}</span>
              {!achieved && planned && <span>{formatShort(event.plannedDate)}</span>}
            </span>
          );
        })}
      </div>

      {/* Floor × stage matrix — FLOOR-scoped stages only */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-xs divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {/* Floor column stays locked while stage columns scroll horizontally */}
              <th className="sticky left-0 z-10 bg-gray-50 border-r border-gray-200 text-left px-3 py-2 text-gray-500 font-medium uppercase tracking-wider whitespace-nowrap">
                Floor
              </th>
              {floorStages.map((stage) => (
                <th
                  key={stage.wbsStageId}
                  className="px-2 py-2 text-gray-500 font-medium uppercase tracking-wider text-center whitespace-nowrap"
                >
                  {stage.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {floors.map((floor) => (
              <tr key={floor} className="group hover:bg-gray-50 transition-colors">
                <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50 border-r border-gray-200 px-3 py-2 font-medium text-gray-700 whitespace-nowrap">
                  {floor === 0 ? "Ground" : `Floor ${floor}`}
                </td>
                {floorStages.map((stage) => (
                  <td key={stage.wbsStageId} className="px-2 py-1.5 text-center">
                    {renderCell(byStageFloor[`${stage.wbsStageId}-${floor}`])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default WbsStatusMatrix;
